<script setup lang="ts">
import { useSeo } from '@/composables/useSeo'
import PageLayout from '@/components/layout/PageLayout.vue'
import Alert from '@/components/ui/Alert.vue'
import Card from '@/components/ui/Card.vue'
import Icon from '@/components/icons/Icon.vue'

useSeo({
  title: 'Disclaimer',
  description:
    'ClearRand outputs are estimates only — not financial, tax, legal or lending advice. Verify important figures with SARS or a qualified professional.',
  path: '/disclaimer',
})

const points = [
  {
    icon: 'info' as const,
    title: 'Estimates only',
    text: 'Outputs depend on your inputs and published assumptions.',
  },
  {
    icon: 'exclamation' as const,
    title: 'Verify before acting',
    text: 'Confirm important figures with a qualified professional.',
  },
  {
    icon: 'x-mark' as const,
    title: 'No liability',
    text: 'We are not liable for loss or damage from reliance on these tools.',
  },
]
</script>

<template>
  <PageLayout
    title="Disclaimer"
    description="Important information about calculator outputs and limitations."
    :breadcrumbs="[{ label: 'Disclaimer' }]"
  >
    <Alert title="Read this first" variant="warning" class="disclaimer-alert">
      <p>
        The calculators and content on this site are provided for general information and planning
        purposes only. They do not constitute financial, tax, legal, lending, estate-agent,
        solar-installation or any other professional advice.
      </p>
    </Alert>

    <div class="section-heading">
      <span class="section-heading__eyebrow">At a glance</span>
      <h2 class="section-heading__title">What this means for you</h2>
    </div>
    <div class="disclaimer-grid">
      <Card v-for="point in points" :key="point.title" padding="lg" raised accent="accent" hover>
        <div class="disclaimer-card__icon">
          <Icon :name="point.icon" :size="24" />
        </div>
        <h2 class="disclaimer-card__title">{{ point.title }}</h2>
        <p class="disclaimer-card__desc">{{ point.text }}</p>
      </Card>
    </div>

    <article class="prose">
      <p><strong>Last reviewed:</strong> 11 September 2026</p>
      <h2>Estimates only</h2>
      <p>
        Every output is an estimate based on the inputs you provide and the assumptions and data
        sources shown on each calculator page. Real-world results depend on many factors we cannot
        know, including lender criteria, individual tax circumstances, changing rates and fees.
      </p>
      <h2>Always verify</h2>
      <p>
        Before making any financial, legal or contractual decision, confirm figures with a qualified
        professional or the relevant institution.
      </p>
      <h2>No liability</h2>
      <p>
        We are not liable for any loss or damage arising from reliance on calculator outputs or
        content on this site.
      </p>
    </article>
  </PageLayout>
</template>

<style scoped>
.disclaimer-alert {
  margin-bottom: var(--space-6);
}

.section-heading {
  margin-bottom: var(--space-5);
}

.section-heading__eyebrow {
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-primary);
}

.section-heading__title {
  font-size: var(--text-2xl);
  font-weight: var(--font-bold);
  margin: 0;
}

.disclaimer-grid {
  display: grid;
  gap: var(--space-4);
  grid-template-columns: 1fr;
  margin-bottom: var(--space-10);
}

@media (min-width: 640px) {
  .disclaimer-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.disclaimer-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, rgb(217 119 6 / 0.12), rgb(251 191 36 / 0.08));
  color: var(--color-accent-dark);
  margin-bottom: var(--space-4);
}

.disclaimer-card__title {
  font-size: var(--text-xl);
  font-weight: var(--font-bold);
  color: var(--color-text);
}

.disclaimer-card__desc {
  margin-top: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
}

.prose {
  max-width: 70ch;
  color: var(--color-text);
  line-height: var(--leading-relaxed);
}

.prose h2 {
  margin-top: var(--space-8);
  font-size: var(--text-xl);
  font-weight: var(--font-semibold);
}

.prose p {
  margin-top: var(--space-3);
}
</style>
